import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtMoney } from "@/lib/api";
import { Loader2, LogOut, Download, ExternalLink, Settings, Layers, ListChecks, Map, Package, Mail } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InlineRentEditor } from "@/components/admin/InlineRentEditor";
import LeaseTemplateEditor from "@/components/admin/LeaseTemplateEditor";
import BookingActionsModal from "@/components/admin/BookingActionsModal";
import SitePlanEditor from "@/components/admin/SitePlanEditor";
import ServicesEditor from "@/components/admin/ServicesEditor";
import LeadsList from "@/components/admin/LeadsList";

const STATUS_COLOR = {
  available: "text-emerald-700",
  pending: "text-amber-700",
  leased: "text-slate-600",
};
const BOOK_STATUS_COLOR = {
  pending_payment: "text-amber-700",
  pending_signature: "text-amber-700",
  active: "text-emerald-700",
  cancelled: "text-red-700",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBooking, setActiveBooking] = useState(null);

  const reload = () =>
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/bookings"),
      api.get("/lots"),
    ]).then(([s, b, l]) => {
      setStats(s.data); setBookings(b.data); setLots(l.data);
    });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin/login"); return; }
    reload().catch((e) => {
      if (e?.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
    }).finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => { localStorage.removeItem("admin_token"); navigate("/admin/login"); };

  const updateLotStatus = async (lot_number, status) => {
    const { data } = await api.patch(`/admin/lots/${lot_number}`, { status });
    setLots((prev) => prev.map((l) => l.lot_number === lot_number ? data : l));
  };
  const onRentSaved = (lot) => {
    setLots((prev) => prev.map((l) => l.lot_number === lot.lot_number ? lot : l));
  };
  const onBookingUpdate = (b) => {
    setBookings((prev) => prev.map((x) => x.id === b.id ? b : x));
    setActiveBooking(b);
    reload().catch(() => {});
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B0C0E]" /></div>;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      <header className="border-b-2 border-[#0B0C0E] bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#0B0C0E] flex items-center justify-center font-black text-[#FACC15] text-sm">14</div>
            <span className="font-display font-black uppercase tracking-tight text-[#0B0C0E]">14 Acres · Admin</span>
          </div>
          <button onClick={logout} className="text-[#56606E] hover:text-[#0B0C0E] text-sm inline-flex items-center gap-2 font-medium" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#0B0C0E] border-2 border-[#0B0C0E] mb-10" data-testid="admin-stats">
            <Stat label="Total Lots" value={stats.total_lots} />
            <Stat label="Available" value={stats.available} accent="text-emerald-700" />
            <Stat label="Pending" value={stats.pending} accent="text-amber-700" />
            <Stat label="Leased" value={stats.leased} accent="text-slate-700" />
            <Stat label="Monthly Revenue" value={fmtMoney(stats.monthly_revenue)} accent="text-[#0B0C0E]" />
          </div>
        )}

        <Tabs defaultValue="lots" className="w-full">
          <TabsList className="bg-transparent border-b-2 border-[#0B0C0E] rounded-none w-full justify-start gap-2 p-0 h-auto" data-testid="admin-tabs">
            <TabsTrigger value="lots" className="rounded-none border-2 border-b-0 border-transparent data-[state=active]:bg-[#0B0C0E] data-[state=active]:text-white data-[state=active]:border-[#0B0C0E] px-5 py-2.5 font-bold text-sm uppercase tracking-wider" data-testid="tab-lots">
              <Layers className="w-4 h-4 mr-2" /> Lots & Pricing
            </TabsTrigger>
            <TabsTrigger value="siteplan" className="rounded-none border-2 border-b-0 border-transparent data-[state=active]:bg-[#0B0C0E] data-[state=active]:text-white data-[state=active]:border-[#0B0C0E] px-5 py-2.5 font-bold text-sm uppercase tracking-wider" data-testid="tab-siteplan">
              <Map className="w-4 h-4 mr-2" /> Site Plan
            </TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-none border-2 border-b-0 border-transparent data-[state=active]:bg-[#0B0C0E] data-[state=active]:text-white data-[state=active]:border-[#0B0C0E] px-5 py-2.5 font-bold text-sm uppercase tracking-wider" data-testid="tab-bookings">
              <ListChecks className="w-4 h-4 mr-2" /> Bookings
            </TabsTrigger>
            <TabsTrigger value="services" className="rounded-none border-2 border-b-0 border-transparent data-[state=active]:bg-[#0B0C0E] data-[state=active]:text-white data-[state=active]:border-[#0B0C0E] px-5 py-2.5 font-bold text-sm uppercase tracking-wider" data-testid="tab-services">
              <Package className="w-4 h-4 mr-2" /> Services
            </TabsTrigger>
            <TabsTrigger value="leads" className="rounded-none border-2 border-b-0 border-transparent data-[state=active]:bg-[#0B0C0E] data-[state=active]:text-white data-[state=active]:border-[#0B0C0E] px-5 py-2.5 font-bold text-sm uppercase tracking-wider" data-testid="tab-leads">
              <Mail className="w-4 h-4 mr-2" /> Leads
            </TabsTrigger>
            <TabsTrigger value="lease" className="rounded-none border-2 border-b-0 border-transparent data-[state=active]:bg-[#0B0C0E] data-[state=active]:text-white data-[state=active]:border-[#0B0C0E] px-5 py-2.5 font-bold text-sm uppercase tracking-wider" data-testid="tab-lease">
              <Settings className="w-4 h-4 mr-2" /> Lease Template
            </TabsTrigger>
          </TabsList>

          {/* LOTS */}
          <TabsContent value="lots" className="mt-8">
            <div className="border-2 border-[#0B0C0E] bg-white overflow-x-auto" data-testid="admin-lots-table">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-[#56606E] bg-[#FCFBF6] border-b border-[#E5E3DA]">
                  <tr>
                    <th className="p-3">Lot</th>
                    <th className="p-3">Size</th>
                    <th className="p-3">Rent (click to edit)</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Quick Change</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((l) => (
                    <tr key={l.lot_number} className="border-t border-[#E5E3DA]">
                      <td className="p-3 font-bold text-[#0B0C0E]">#{l.lot_number}</td>
                      <td className="p-3 text-[#2C2F36]">{l.sq_ft.toLocaleString()} sq ft · {l.acres.toFixed(2)} ac</td>
                      <td className="p-3 text-[#2C2F36]">
                        <InlineRentEditor lot={l} onSave={onRentSaved} />
                      </td>
                      <td className={`p-3 uppercase font-bold ${STATUS_COLOR[l.status]}`}>{l.status}</td>
                      <td className="p-3">
                        <select
                          value={l.status} onChange={(e) => updateLotStatus(l.lot_number, e.target.value)}
                          className="bg-white border border-[#C6C3B5] text-xs px-2 py-1 text-[#0B0C0E]"
                          data-testid={`admin-lot-status-${l.lot_number}`}
                        >
                          <option value="available">available</option>
                          <option value="pending">pending</option>
                          <option value="leased">leased</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-8">
            <div className="border-2 border-[#0B0C0E] bg-white overflow-x-auto" data-testid="admin-bookings-table">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-[#56606E] bg-[#FCFBF6] border-b border-[#E5E3DA]">
                  <tr>
                    <th className="p-3">Created</th><th className="p-3">Lot</th><th className="p-3">Tenant</th>
                    <th className="p-3">Email</th><th className="p-3">Total</th><th className="p-3">Status</th><th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-[#E5E3DA]">
                      <td className="p-3 text-[#56606E] text-xs">{new Date(b.created_at).toLocaleString()}</td>
                      <td className="p-3 font-bold text-[#0B0C0E]">#{b.lot_number}</td>
                      <td className="p-3 text-[#0B0C0E]">{b.tenant_name}{b.company ? ` · ${b.company}` : ""}</td>
                      <td className="p-3 text-[#2C2F36]">{b.tenant_email}</td>
                      <td className="p-3 text-[#0B0C0E]">{fmtMoney(b.total_due)}</td>
                      <td className={`p-3 uppercase font-bold text-xs ${BOOK_STATUS_COLOR[b.status]}`}>
                        {b.status.replace("_", " ")}
                        {b.refund_status === "refunded" && <span className="ml-2 text-red-700">· REFUNDED</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-3 items-center">
                          <button
                            onClick={() => setActiveBooking(b)}
                            className="border border-[#0B0C0E] hover:bg-[#FACC15] text-[#0B0C0E] px-3 py-1 text-xs uppercase font-bold"
                            data-testid={`open-actions-${b.id}`}
                          >Manage</button>
                          <a href={`${API}/bookings/${b.id}/lease.pdf`} target="_blank" rel="noreferrer" className="text-[#0B0C0E] hover:underline underline-offset-4 decoration-[#FACC15] decoration-2 inline-flex items-center gap-1 text-xs font-medium" data-testid={`admin-pdf-${b.id}`}>
                            <Download className="w-3 h-3" /> PDF
                          </a>
                          <a href={`/booking/${b.id}`} target="_blank" rel="noreferrer" className="text-[#2C2F36] hover:text-[#0B0C0E] inline-flex items-center gap-1 text-xs">
                            <ExternalLink className="w-3 h-3" /> View
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!bookings.length && (
                    <tr><td colSpan="7" className="p-8 text-center text-[#97A1AE]">No bookings yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* LEASE TEMPLATE */}
          <TabsContent value="lease" className="mt-8">
            <LeaseTemplateEditor />
          </TabsContent>

          {/* SITE PLAN EDITOR */}
          <TabsContent value="siteplan" className="mt-8">
            <SitePlanEditor />
          </TabsContent>

          {/* SERVICES */}
          <TabsContent value="services" className="mt-8">
            <ServicesEditor />
          </TabsContent>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-8">
            <LeadsList />
          </TabsContent>
        </Tabs>
      </div>

      {activeBooking && (
        <BookingActionsModal
          booking={activeBooking}
          onClose={() => setActiveBooking(null)}
          onUpdate={onBookingUpdate}
        />
      )}
    </div>
  );
}

const Stat = ({ label, value, accent = "text-[#0B0C0E]" }) => (
  <div className="bg-white p-6">
    <div className="overline">{label}</div>
    <div className={`mt-2 font-display font-black text-3xl ${accent}`}>{value}</div>
  </div>
);
